import {
  Card,
  Descriptions,
  Tag,
  Image,
  Typography,
  Space,
  Statistic,
  Row,
  Col,
  Divider,
  Button,
  Modal,
  message,
  Result,
} from "antd";
import {
  ClockCircleOutlined,
  UserOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  LockOutlined,
  AimOutlined,
  CheckCircleOutlined,
  RollbackOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import Loading from "../components/Loading";
import { useParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import {
  colorStatusInfraction,
  formatDateTime,
  SOURCE_TYPES,
  statusText,
} from "../utils/constants";
import { getInfraction, blockInfraction, unblockInfraction } from "../api/infractions/infractionRequest";
import CancelInfraction from "../components/CancelInfraction";
import { IMAGE_URL } from "../constants";
import { countries } from "../utils/countries";
import PermissionRender from "../components/PermissionRender";
import ViolationPdf from "../components/InfractionDetail/ViolationPdf";
import html2canvas from "html2canvas";

const { Title, Text } = Typography;
export default function DetailsInfraction() {
  const [loading, setLoading] = useState(true);
  const [infraction, setInfraction] = useState({});
  
  const [notFound, setNotFound] = useState(false);
  const { id } = useParams();
  const [isPdfModalVisible, setIsPdfModalVisible] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const pageRef = useRef();

  const fetchInfraction = async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const data = await getInfraction(id);
      setInfraction(data);
    } catch (error) {
      console.error(error);
      if (error.response && error.response.status === 404) {
        setNotFound(true);
        message.error("L'infraction demandée n'a pas été trouvée");
      } else {
        message.error("Une erreur s'est produite lors du chargement de l'infraction");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInfraction();
  }, [id]);

  

  const handlePdfModalOpen = async () => {
    try {
      // Capture screenshot before opening the modal
      const element =
        pageRef.current || document.querySelector(".min-h-screen.bg-purple-50");
      if (element) {
        const canvas = await html2canvas(element, {
          useCORS: true,
          allowTaint: true,
          scale: 0.8,
          width: element.scrollWidth,
          height: element.scrollHeight,
        });
        setScreenshot(canvas.toDataURL("image/png"));
      }
      setIsPdfModalVisible(true);
    } catch (error) {
      console.error("Error capturing screenshot:", error);
      setIsPdfModalVisible(true);
    }
  };

  const renderStatusInfo = () => {
    if (infraction.status === "paid" && infraction?.payment) {
      return (
        <Card size="small" className="mb-6 border-green-500">
          <div className="flex items-center">
            <CheckCircleOutlined className="text-green-500 text-xl mr-2" />
            <div className="w-full">
              <Text strong>
                {infraction.payment.source === "citizen_web" || infraction.payment.source === "citizen_mobile"
                  ? `Payée via ${SOURCE_TYPES[infraction.payment.source]}`
                  : "Payée par:"}
              </Text>
              <div className="flex items-center mt-1">
                {infraction.payment.source === "citizen_web"  || infraction.payment.source === "citizen_mobile" ? (
                  <>
                    <Tag color="blue" className="mr-2">
                      {SOURCE_TYPES[infraction.payment.source]}
                    </Tag>
                    <Text>Paiement automatique</Text>
                  </>
                ) : (
                  <>
                    <UserOutlined className="text-green-500 mr-2" />
                    <Text>{infraction.payment.user?.full_name || "Agent"}</Text>
                  </>
                )}
                <Text type="secondary" className="ml-2">
                  {formatDateTime(infraction.payment.payment_date)}
                </Text>
              </div>
              {infraction.payment.reference_number && (
                <div className="flex items-center mt-1">
                  <Text strong className="mr-2">
                    Référence:
                  </Text>
                  <Text code>{infraction.payment.reference_number}</Text>
                </div>
              )}
              {infraction.payment.payment_method && (
                <div className="flex items-center mt-1">
                  <Text strong className="mr-2">
                    Méthode:
                  </Text>
                  <Tag
                    color={
                      infraction.payment.payment_method === "cmi" || infraction.payment.payment_method === "fatourati"
                        ? "blue"
                        : "green"
                    }
                  >
                    {infraction.payment.payment_method.toUpperCase()}
                  </Tag>
                </div>
              )}
            </div>
          </div>
        </Card>
      );
    }

    if (infraction.status === "blocked") {
      return (
        <Card size="small" className="mb-6 border-orange-500">
          <div className="flex items-center">
            <LockOutlined className="text-orange-500 text-xl mr-2" />
            <div>
              <Text strong>Bloquée par:</Text>
              <div className="flex items-center mt-1">
                <UserOutlined className="text-orange-500 mr-2" />
                {infraction.blocked_by && (
                  <Text>{infraction.blocked_by.full_name}</Text>
                )}
                <Text type="secondary" className="ml-2">
                  {formatDateTime(infraction.blocked_at)}
                </Text>
              </div>
            </div>
          </div>
        </Card>
      );
    }

    if (infraction.status === "canceled") {
      return (
        <Card size="small" className="mb-6 border-red-500">
          <div className="flex items-center">
            <StopOutlined className="text-red-500 text-xl mr-2" />
            <div>
              <Text strong>Annulée par:</Text>
              <div className="flex items-center mt-1">
                <UserOutlined className="text-red-500 mr-2" />
                {infraction.cancelled_by && (
                  <Text>{infraction.cancelled_by.full_name}</Text>
                )}
                <Text type="secondary" className="ml-2">
                  {formatDateTime(infraction.cancelled_at)}
                </Text>
              </div>
              {infraction.cancellation_reason && (
                <div className="mt-2">
                  <Text strong>Motif:</Text>
                  <Text className="ml-2">
                    {infraction.cancellation_reason.name}
                  </Text>
                </div>
              )}
            </div>
          </div>
        </Card>
      );
    }

    return null;
  };

  if (loading) return <Loading />;

  if (notFound) {
    return (
      <div className="min-h-screen bg-purple-50 p-8 flex justify-center">
        <Card className="max-w-lg w-full shadow-lg border-t-4 border-red-600">
          <Result
            status="404"
            title="Infraction non trouvée"
            subTitle={`L'infraction avec la référence #${id} n'existe pas ou a été supprimée.`}
            extra={
              <Button
                type="primary"
                onClick={() => window.history.back()}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Retour
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-purple-50 p-8">
      <Card
        ref={pageRef}
        className="max-w-4xl mx-auto shadow-lg border-t-4 border-purple-600"
      >
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Title level={2} className="text-purple-700 m-0">
              Détails de l'Infraction #{infraction.reference}{" "}
              {infraction.warning_id && (
                <Button
                  icon={<RollbackOutlined />}
                  onClick={() =>
                    window.open(`/warning/${infraction?.warning_id}`, "_blank")
                  }
                />
              )}
            </Title>
            <Tag
              color={colorStatusInfraction[infraction.status]}
              className="text-lg px-4 py-1"
            >
              {statusText(infraction.status)}
            </Tag>
          </div>
          <div className="flex gap-2">
            <Button
              type="default"
              icon={<PrinterOutlined />}
              size="small"
              onClick={handlePdfModalOpen}
              className="bg-purple-500 text-white hover:bg-purple-600"
            >
              Version Arabe
            </Button>
              {infraction.status === "pending" && (
                <>
                  <PermissionRender requiredPermission={["infractions", "cancel"]}>
                    <CancelInfraction infractionId={id} onCancelled={fetchInfraction} />
                  </PermissionRender>
                  <PermissionRender requiredPermission={["infractions", "block"]}>
                    <Button
                      type="default"
                      icon={<LockOutlined />}
                      size="small"
                      className="bg-orange-500 text-white hover:bg-orange-600"
                      onClick={() => {
                        Modal.confirm({
                          title: "Bloquer cette infraction ?",
                          content: "L'infraction ne pourra plus être payée tant qu'elle est bloquée.",
                          okText: "Bloquer",
                          cancelText: "Annuler",
                          onOk: async () => {
                            try {
                              await blockInfraction(id);
                              message.success("Infraction bloquée avec succès");
                              fetchInfraction();
                            } catch (error) {
                              message.error(error.response?.data?.message || "Erreur lors du blocage");
                            }
                          },
                        });
                      }}
                    >
                      Bloquer
                    </Button>
                  </PermissionRender>
                </>
              )}
              {infraction.status === "blocked" && (
                <PermissionRender requiredPermission={["infractions", "unblock"]}>
                  <Button
                    type="default"
                    icon={<LockOutlined />}
                    size="small"
                    className="bg-green-500 text-white hover:bg-green-600"
                    onClick={() => {
                      Modal.confirm({
                        title: "Débloquer cette infraction ?",
                        content: "L'infraction redeviendra payable.",
                        okText: "Débloquer",
                        cancelText: "Annuler",
                        onOk: async () => {
                          try {
                            await unblockInfraction(id);
                            message.success("Infraction débloquée avec succès");
                            fetchInfraction();
                          } catch (error) {
                            message.error(error.response?.data?.message || "Erreur lors du déblocage");
                          }
                        },
                      });
                    }}
                  >
                    Débloquer
                  </Button>
                </PermissionRender>
              )}
          </div>
        </div>

        {renderStatusInfo()}

        <Row gutter={24}>
          <Col span={14}>
            <Statistic
              title="Type d'Infraction"
              value={infraction.type_infraction.name}
              valueStyle={{ color: "#722ed1" }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Col>
          <Col span={10}>
            <Statistic
              title="Montant de l'Amende"
              value={infraction.amount + " MAD"}
              prefix=""
              valueStyle={{ color: "#722ed1" }}
            />
          </Col>
        </Row>

        <Divider className="my-6" />

        <Descriptions column={1} className="mb-6">
          <Descriptions.Item
            label={
              <Text strong className="text-purple-700">
                Description
              </Text>
            }
          >
            {infraction.description}
          </Descriptions.Item>
        </Descriptions>

        <Row gutter={24} className="mb-6">
          <Col span={12}>
            <Card
              size="small"
              title={
                <Text strong className="text-purple-700">
                  Informations du Véhicule
                </Text>
              }
              className="h-full"
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Matricule">
                  {infraction.matricule} {" ( "}
                  {
                    countries.find(
                      (c) => c.code === infraction?.vehicle?.country
                    )?.name
                  }
                  {" ) "}
                </Descriptions.Item>
                <Descriptions.Item label="Marque">
                  {infraction.brand}
                </Descriptions.Item>
                <Descriptions.Item label="Modèle">
                  {infraction.model}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col span={12}>
            <Card
              size="small"
              title={
                <Text strong className="text-purple-700">
                  Détails de l'Infraction
                </Text>
              }
              className="h-full"
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item
                  label={
                    <div className="flex items-center">
                      <ClockCircleOutlined className="mr-2" />
                      <span>Date et Heure</span>
                    </div>
                  }
                >
                  {formatDateTime(infraction.datetime_infraction)}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <div className="flex items-center">
                      <AimOutlined className="mr-2" />
                      <span>Contrat</span>
                    </div>
                  }
                >
                  {infraction?.zone?.name}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <div className="flex items-center">
                      <AimOutlined className="mr-2" />
                      <span>Zone</span>
                    </div>
                  }
                >
                  {infraction?.section?.name || "Non défini"}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <div className="flex items-center">
                      <AimOutlined className="mr-2" />
                      <span>Rue</span>
                    </div>
                  }
                >
                  {infraction?.street?.name || "Non défini"}
                  {" - "}
                  {infraction?.street?.name_ar || "غير محدد"}
                </Descriptions.Item>

                <Descriptions.Item label="Coordonnées">
                  <a
                    href={`https://www.google.com/maps?q=${infraction.latitude},${infraction.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#722ed1", textDecoration: "underline" }}
                  >
                    Lat: {infraction.latitude}, Long: {infraction.longitude}
                  </a>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>

        <Card
          size="small"
          title={
            <Text strong className="text-purple-700">
              Preuves
            </Text>
          }
          className="mb-6"
        >
          <Space direction="vertical" size="middle" className="w-full">
            {infraction.photos && (
              <div>
                <Text strong className="mr-3">
                  Photos:
                </Text>
                <Image.PreviewGroup>
                  <Space size={[8, 8]} wrap>
                    {infraction.photos.split(",").map((photo, index) => (
                      <Image
                        key={index}
                        width={150}
                        height={150}
                        src={IMAGE_URL + photo}
                        alt={`Photo d'infraction ${index + 1}`}
                        className="object-cover rounded"
                      />
                    ))}
                  </Space>
                </Image.PreviewGroup>
              </div>
            )}
            <div className="flex justify-between items-center">
              <div></div>
              <div>
                <Text strong>Agent Verbalisateur:</Text>
                <div className="flex items-center mt-2">
                  <UserOutlined className="text-purple-600 text-xl mr-2" />
                  <Text>{infraction?.user?.full_name}</Text>
                </div>
              </div>
            </div>
          </Space>
        </Card>

      </Card>
      <ViolationPdf
        visible={isPdfModalVisible}
        onClose={() => setIsPdfModalVisible(false)}
        formValues={infraction}
        screenshot={screenshot}
      />
    </div>
  );
}
